import { createBrowserHistory, History } from 'history';
import NotificationActions from '../../Actions/NotificationActions';
import NavigationActions from '../../Actions/NavigationActions';
import NavigationStore from '../../Stores/NavigationStore';
import Browser from '../../Utilities/Browser';

const unsavedChangedMessage = "You have unsaved changes. Would you like to discard them?";

// This type really belongs in MessageView.jsx, but that isn't yet migrated to TypeScript
type NotificationStoreNotification = {
    title?: string;
    messages: string[];
    icon?: string;
    severity?: '' | 'information' | 'success' | 'failure';
    closeable?: boolean;
    onClose?: () => void;
};

type NotificationState = {
    notification?: NotificationStoreNotification;
};

let history: History<NotificationState | null> | undefined = undefined;

// Difference between `history.block` and `window.addEventListener("beforeunload")`
//
// Both `history.block` (for in SPA navigation via the router) and `window.addEventListener("beforeunload")`
// (for navigation that leaves the SPA e.g. closing the tab or entering a different URL) is used. They appear
// to do similar things but are necessary for different navigation scenarios.
// - `history.block` intercepts client side route changes inside the single page app.
// - `beforeunload` covers browser level navigation events that bypass the React Router entirely.

// Custom code for listening to and blocking client side route changes inside the single page app.
//
// Mention of app in this comment block refers to App/Consignments or App/Shipping.
// This custom code is tightly coupled to the React Router component and is specific to each app.
// It is wired up in the function getAppRouter in Routes.tsx. The custom code allows us to run custom logic
// (e.g. clearing notifications) on navigation events, and to block navigation when there are unsaved changes.
//
// Navigation within the app triggers the custom code, while external navigation is handled by the browser. For instance making a change to
// a charge type in App/Admin/ChargeTypes and clicking the close button on the page triggers the custom code. When the cancel button is clicked the code explicitly calls
// 'navigate("/ChargeTypes/")'. The confirm dialog displays the CUSTOM message 'You have unsaved changes. Would you like to discard them?'
// While clicking the browser back button triggers the browser's built in navigation handling. The confirm dialog displays 'Changes you made may not be saved.'
//
// Another observation is if you go to a consignment via the consignment list page and cut the URL e.g https://int.ecargo.net/App/Consignments/Consignment/15828761 and then go to say the
// Change password page. Paste the url into the browser. Hit enter to reload the pasted URL. On pressing the back button on the page it will take you back to the Change password page not the consignment list page.
// If you select a charge type in the charge type list page and cut the URL e.g. https://int.ecargo.net/App/Admin/ChargeTypes/3025 and then go to the Change password page.
// Paste the url into the browser. Hit enter to reload the pasted URL. On pressing the back button on the page it will take you back to the charge type list
// page. This is because the back button on the charge type page is setup to navigate directly to the charge type list page.

export function initialiseHistory(basename: string) {
    if(history !== undefined)
        throw new Error("You must only call the initialise function once.");

    history = createBrowserHistory<NotificationState | null>({ basename });

    history.listen((location) => {
        NavigationActions.navigatedAway();

        //grandfather in the legacy behaviour for the shipping schedule pages
        if (basename === "/App/Shipping/" || (basename === "/App/Admin/" && location.pathname.toLowerCase().startsWith("/chargetypes"))) {
            NotificationActions.clearNotifications();
            if (location.state) {
                const state = location.state;

                if (state.notification)
                    NotificationActions.notificationReceived(state.notification);
            }
        }
    });

    // This lets you register a prompt message that will be shown to the user for in app (SPA) navigation.
    // The window.confirm message will be the string returned from the callback. If the user clicks "OK" they will
    // navigate to the new page, if they click "Cancel" they will stay on the current page. If no string is returned
    // from the callback then the user will navigate to the new page without a confirmation dialog.
    // See https://github.com/remix-run/history/blob/v4/docs/Blocking.md for more details.
    history.block(() => {
        if(NavigationStore.getState().isEntityModified) {
            return unsavedChangedMessage;
        }
    });
}

/*
 In newer browsers when showing browser dialogs (alert/confirm/prompt) the user has the option of suppressing further dialogs.
 If the user clicks this and then clicks cancel, then any futher confirms will be sent back as false.
 window.confirm is used by react router to confirm navigating away when there are unsaved changes.
 If the user supresses further dialogs and clicks cancel then they will no longer be able to navigate away for that session.
 To prevent this we need to figure out if the dialog was "automatically" responded to.
 If we receive an answer back in less than 20 milliseconds we can "safely" assume that the dialog was not shown.
 If we receive an automated answer we want to perform the navigation.
*/

if(Browser.window === undefined)
    throw new Error("The browser window is not defined. This code should only be run in a browser environment.");

const confirm = Browser.window.confirm;
Browser.window.confirm = function(...args) {
    const callTime = new Date();
    const response = confirm.apply(Browser.window, args);
    const responseTime = new Date();
    const responseDelta = responseTime.valueOf() - callTime.valueOf();

    if(responseDelta < 20)
        return true;
    else
        return response;
};

// Even though this returns 'unsavedChangedMessage' in 'window.addEventListener("beforeunload"' it will fallback to displaying the
// browser's default message 'Changes you made may not be saved.' For security reasons, modern browsers no longer support custom messages in
// beforeunload dialogs.
let warnBeforeUnload: () => string | undefined = () => {
    if(NavigationStore.getState().isEntityModified) {
        return unsavedChangedMessage;
    }
};

const setWarnBeforeUnload = (warner: () => string | undefined) => {
    warnBeforeUnload = warner;
};

window.addEventListener("beforeunload", (ev) => {
    const warning = warnBeforeUnload();
    if (warning){
        ev.preventDefault();
        return ev.returnValue = warning;
    }
});

const getHistory = () => {
    if(history === undefined)
        throw new Error("You must call initialise before using push.");
    return history;
};

const historyPush: History<NotificationState | null>['push'] = (...args) => {
    return getHistory().push(...args);
};

const navigateBackWithUnsavedChangesCheck = () => {
    const hasUnsavedChanges = NavigationStore.getState().isEntityModified;

    if(hasUnsavedChanges && !window.confirm(unsavedChangedMessage)) {
        // If there are unsaved changes and the user does not confirm, we do not navigate back.
        return;
    }

    if(hasUnsavedChanges) {
        // The user has confirmed they want to discard unsaved changes.
        // Need to tell the NavigationStore that we are navigating away so to prevent the unsaved changes dialog
        // from appearing a second time.
        NavigationActions.navigatedAway();
    }

    NotificationActions.clearNotifications();
    getHistory().goBack();
};

export default {
    push: historyPush,
    getHistory,
    setWarnBeforeUnload,
    navigateBackWithUnsavedChangesCheck
};
